import { Room } from '../types';
import { legacyNegativeId, safeNegativeId } from '../utils/idUtils';

type OfflineRoomSyncStatus = 'pending' | 'syncing' | 'synced' | 'failed';

type OfflineRoomRecord = {
  temp_id: string;
  property_id: number;
  name: string;
  display_order?: number;
};

type SqlResult = {
  rows: {
    length: number;
    item(index: number): any;
  };
};

export interface OfflineRoomSyncDatabase {
  createOfflineRoom(propertyId: number, name: string, displayOrder?: number): Promise<string>;
  getPendingOfflineRooms(): Promise<OfflineRoomRecord[]>;
  updateOfflineRoomSyncStatus(
    tempId: string,
    syncStatus: OfflineRoomSyncStatus,
    syncError?: string,
    realId?: number
  ): Promise<void>;
  getCachedRooms?(propertyId: number): Promise<Room[]>;
  cacheRooms?(rooms: Room[], propertyId?: number, ttlHours?: number): Promise<void>;
  saveIdMapping(tempId: string, realId: number, entityType: 'room'): Promise<void>;
  updateResponseRoomIdMapping(oldRoomId: number, newRoomId: number): Promise<void>;
  executeSql(sql: string, params?: any[]): Promise<SqlResult>;
  getReview(reviewId: number): Promise<{ syncStatus?: string } | null>;
  updateReview(reviewId: number, updates: { syncStatus?: string; syncError?: string }): Promise<void>;
  updateCachedRoom(propertyId: number, oldId: number, newId: number): Promise<void>;
}

export interface OfflineRoomApi {
  createRoom(propertyId: number, name: string, options?: { forceOnline?: boolean }): Promise<Room>;
}

export type OfflineRoomIdMapper = (
  tempId: string,
  realId: number,
  entityType: 'room'
) => Promise<void>;

export class OfflineRoomSyncService {
  constructor(
    private readonly database: OfflineRoomSyncDatabase,
    private readonly roomApi: OfflineRoomApi,
    private readonly mapTempIdToRealId: OfflineRoomIdMapper
  ) {}

  async createOfflineRoom(propertyId: number, name: string, displayOrder: number = 0): Promise<string> {
    return this.database.createOfflineRoom(propertyId, name, displayOrder);
  }

  async syncPendingRooms(): Promise<void> {
    const pendingRooms = await this.database.getPendingOfflineRooms();

    for (const room of pendingRooms) {
      await this.syncRoom(room);
    }
  }

  private async syncRoom(room: OfflineRoomRecord): Promise<void> {
    try {
      await this.database.updateOfflineRoomSyncStatus(room.temp_id, 'syncing');

      const createdRoom = await this.roomApi.createRoom(room.property_id, room.name, { forceOnline: true });
      const oldNegativeId = safeNegativeId(room.temp_id);

      await this.cacheSyncedRoom(room, createdRoom);
      await this.database.updateOfflineRoomSyncStatus(room.temp_id, 'synced', undefined, createdRoom.id);
      await this.database.saveIdMapping(room.temp_id, createdRoom.id, 'room');
      await this.mapTempIdToRealId(room.temp_id, createdRoom.id, 'room');
      await this.remapRoomResponses(room.temp_id, oldNegativeId, createdRoom.id);
      await this.requeueResponsesForRoom(createdRoom.id);
      await this.database.updateCachedRoom(room.property_id, oldNegativeId, createdRoom.id);
    } catch (error) {
      console.error('[OfflineRoomSyncService] ❌ Room sync failed:', room.name, error);
      await this.database.updateOfflineRoomSyncStatus(
        room.temp_id,
        'failed',
        error instanceof Error ? error.message : 'Unknown error'
      );
    }
  }

  private async cacheSyncedRoom(room: OfflineRoomRecord, createdRoom: Room): Promise<void> {
    if (!this.database.getCachedRooms || !this.database.cacheRooms) {
      return;
    }

    try {
      const currentRooms = await this.database.getCachedRooms(room.property_id).catch(() => []);
      const roomToCache: Room = {
        id: createdRoom.id,
        propertyId: room.property_id,
        name: createdRoom.name || room.name,
        displayOrder: createdRoom.displayOrder || 0,
        isCustom: true,
      };

      await this.database.cacheRooms([...currentRooms, roomToCache], room.property_id, 24);
    } catch (cacheErr) {
      console.error('[OfflineRoomSyncService] Failed to cache synced room:', cacheErr);
    }
  }

  private async remapRoomResponses(tempId: string, oldNegativeId: number, realRoomId: number): Promise<void> {
    await this.database.updateResponseRoomIdMapping(oldNegativeId, realRoomId);

    const legacyId = legacyNegativeId(tempId);
    if (legacyId !== oldNegativeId) {
      await this.database.updateResponseRoomIdMapping(legacyId, realRoomId).catch(() => {
        // Legacy ID might not exist, that's OK.
      });
    }
  }

  private async requeueResponsesForRoom(roomId: number): Promise<void> {
    try {
      await this.database.executeSql(
        `UPDATE responses SET sync_status = ?, sync_error = NULL WHERE room_id = ?`,
        ['pending', roomId]
      );

      const affectedResponses = await this.database.executeSql(
        `SELECT DISTINCT review_id FROM responses WHERE room_id = ?`,
        [roomId]
      );

      for (let i = 0; i < affectedResponses.rows.length; i++) {
        const reviewId = affectedResponses.rows.item(i).review_id;
        const review = await this.database.getReview(reviewId);

        if (review && review.syncStatus === 'failed') {
          await this.database.updateReview(reviewId, {
            syncStatus: 'pending',
            syncError: undefined,
          });
        }
      }
    } catch (requeueError) {
      console.error('[OfflineRoomSyncService] Error requeueing responses:', requeueError);
    }
  }
}
